import { PDFDocument, PDFCheckBox, PDFDict, PDFArray, PDFName, PDFNumber, PDFButton } from 'pdf-lib'
import type { ICustomInput } from '../features/fillablePDFs/store/fillablePDFs.types'
import { inputTypeLookup } from '../features/fillablePDFs/store/fillablePDFs.types'
import Logger from '../services/logger.service'

export const uint8ToArrayBuffer = (u8: Uint8Array): ArrayBuffer => {
  const ab = new ArrayBuffer(u8.byteLength)
  new Uint8Array(ab).set(u8)
  return ab
}

const isTruthy = (v: any) =>
  v === true || v === 1 || v === '1' || v === 'true' || v === 'Yes' || v === 'yes' || v === 'on'

const resolveAnswerId = (ci: ICustomInput): any => {
  if (ci.fieldType === 'signature') {
    return ci.answerId
  }
  if (ci.answerId !== null && typeof ci.answerId === 'object' && 'name' in ci.answerId) {
    return (ci.answerId as { name: string }).name
  }

  return ci.answerId
}

export const resolveInputsForPdf = (inputs: ICustomInput[]): ICustomInput[] =>
  inputs.map((ci) => ({ ...ci, answerId: resolveAnswerId(ci) }))

const getRectFromWidget = (widget: PDFDict): { x: number; y: number; width: number; height: number } | null => {
  const rect = widget.lookup(PDFName.of('Rect'), PDFArray)
  if (!rect || rect.size() < 4) return null

  const x1 = (rect.lookup(0, PDFNumber) as PDFNumber).asNumber()
  const y1 = (rect.lookup(1, PDFNumber) as PDFNumber).asNumber()
  const x2 = (rect.lookup(2, PDFNumber) as PDFNumber).asNumber()
  const y2 = (rect.lookup(3, PDFNumber) as PDFNumber).asNumber()

  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  }
}

const drawSignatureForField = async (pdfDoc: PDFDocument, form: any, fieldName: string, dataUrl: string) => {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl
  const pngImage = await pdfDoc.embedPng(base64)

  try {
    const button = form.getButton(fieldName) as PDFButton
    button.setImage(pngImage)
    return
  } catch (error) {
    Logger.error('Error in drawSignatureForField:' + error)
  }

  const field =
    form.getFieldMaybe?.(fieldName) ??
    (() => {
      try {
        return form.getField(fieldName)
      } catch {
        return null
      }
    })()

  if (!field) return

  const acroField = field.acroField
  const widgets = acroField?.getWidgets?.() ?? []

  for (const widget of widgets) {
    const rect = getRectFromWidget(widget.dict)
    if (!rect) continue

    const pageRef = widget.P()
    const page = pdfDoc.getPages().find((p: any) => p.ref === pageRef)

    if (!page) continue

    const padding = rect.width * 0.4
    const usableWidth = rect.width - padding
    const usableHeight = rect.height

    let scale = usableWidth / pngImage.width
    const drawWidth = pngImage.width * scale
    let drawHeight = pngImage.height * scale

    if (drawHeight > usableHeight * 1.2) {
      scale = (usableHeight * 1.2) / pngImage.height
      drawHeight = pngImage.height * scale
    }

    const x = rect.x
    const y = rect.y + (rect.height - drawHeight) / 1.5

    page.drawImage(pngImage, {
      x,
      y,
      width: drawWidth,
      height: drawHeight,
    })
  }
}

export const fillPdfWithCustomInputs = async (pdfBase64: string, inputs: ICustomInput[]): Promise<ArrayBuffer> => {
  const resolvedInputs = resolveInputsForPdf(inputs)
  const clean = pdfBase64.includes(',') ? pdfBase64.split(',')[1] : pdfBase64
  const binary = atob(clean)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)

  const pdfDoc = await PDFDocument.load(bytes)
  const form = pdfDoc.getForm()

  const hasCheckedToggle = resolvedInputs.some((ci) => ci.fieldType === inputTypeLookup.Toggle && isTruthy(ci.answerId))
  if (hasCheckedToggle) {
    form
      .getFields()
      .filter((f): f is PDFCheckBox => f instanceof PDFCheckBox)
      .forEach((f) => f.uncheck())
  }

  const initialMultiSelectOptionLists = new Set<string>()

  for (const ci of resolvedInputs) {
    const acroName = ci.question
    const val = ci.answerId
    if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) continue
    if (!acroName) continue
    try {
      if (ci.fieldType === 'signature') {
        const signatureValue = typeof val === 'string' ? val : val?.trimmedDataUrl ?? val?.originalDataUrl

        if (typeof signatureValue === 'string') {
          await drawSignatureForField(pdfDoc, form, acroName, signatureValue)
        }
      } else if (ci.fieldType === inputTypeLookup.Toggle) {
        if (!isTruthy(val)) continue
        try {
          form.getCheckBox(acroName).check()
        } catch {
          form.getRadioGroup(acroName).select(String(val))
        }
      } else if (ci.fieldType === inputTypeLookup.SingleSelect) {
        try {
          const dd = form.getDropdown(acroName)
          dd.select(String(val))
        } catch {
          try {
            const list = form.getOptionList(acroName)
            list.select(String(val))
          } catch {
            const rg = form.getRadioGroup(acroName)
            rg.select(String(val))
          }
        }
      } else if (ci.fieldType === inputTypeLookup.MultiSelect) {
        try {
          const list = form.getOptionList(acroName)
          const merge = initialMultiSelectOptionLists.has(acroName)
          list.select(String(val), merge)
          initialMultiSelectOptionLists.add(acroName)
        } catch {
          const cb = form.getCheckBox(String(val))
          cb.check()
        }
      } else {
        const tf = form.getTextField(acroName)
        tf.setText(String(val))
      }
    } catch (error) {
      Logger.error('Error in fillPdfWithCustomInputs:' + error)
    }
  }

  form.flatten()
  const saved = await pdfDoc.save()
  return uint8ToArrayBuffer(saved)
}
